import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  libraryModuleFormSchema,
  LibraryModuleFormData,
} from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleForm/types";
import {
  emptyLibraryModuleFormData,
  formDataToLibraryModulePayload,
  libraryModuleToFormData,
} from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleForm/mapper";
import {
  getLibraryModuleAction,
  updateLibraryModuleAction,
  resetCreateUpdateLibraryModuleState,
} from "@/modules/learningCenter/features/ModuleLibrary/slices/createUpdateLibraryModuleSlice";
import {
  publishLibraryModuleAction,
  archiveLibraryModuleAction,
} from "@/modules/learningCenter/features/ModuleLibrary/slices/libraryModuleListSlice";

export const useEditLibraryModule = (uuid: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const module = useAppSelector(
    (state: RootState) => state.createUpdateLibraryModule.fetchedModule,
  );
  const loading = useAppSelector(
    (state: RootState) => state.createUpdateLibraryModule.loadingGet,
  );
  const error = useAppSelector(
    (state: RootState) => state.createUpdateLibraryModule.getError,
  );
  const isSubmitting = useAppSelector(
    (state: RootState) => state.createUpdateLibraryModule.loadingUpdate,
  );

  const form = useForm<LibraryModuleFormData>({
    resolver: zodResolver(libraryModuleFormSchema),
    defaultValues: emptyLibraryModuleFormData(),
    mode: "onChange",
  });

  useEffect(() => {
    dispatch(getLibraryModuleAction(uuid));
    return () => {
      dispatch(resetCreateUpdateLibraryModuleState());
    };
  }, [dispatch, uuid]);

  useEffect(() => {
    if (module && module.uuid === uuid) {
      form.reset(libraryModuleToFormData(module));
    }
  }, [module, uuid, form]);

  const submitForm = form.handleSubmit((data) => {
    dispatch(
      updateLibraryModuleAction(
        uuid,
        formDataToLibraryModulePayload(data),
        (updated) => {
          showNotification(
            "Module saved",
            `"${updated.title}" was updated.`,
            "success",
          );
          dispatch(getLibraryModuleAction(uuid));
        },
      ),
    );
  });

  const handleCancel = () => {
    navigate("/admin/learning-center/module-library");
  };

  const handlePublish = () => {
    dispatch(
      publishLibraryModuleAction(uuid, () => {
        showNotification(
          "Module published",
          "The module is now published.",
          "success",
        );
        dispatch(getLibraryModuleAction(uuid));
      }),
    );
  };

  const handleArchive = () => {
    dispatch(
      archiveLibraryModuleAction(uuid, () => {
        showNotification(
          "Module archived",
          "The module has been archived.",
          "success",
        );
        navigate("/admin/learning-center/module-library");
      }),
    );
  };

  return {
    form,
    module,
    loading,
    error,
    isSubmitting,
    submitForm,
    handleCancel,
    handlePublish,
    handleArchive,
  };
};
